import { TypesenseData } from '@/lib/typesenseFunction'
import { CompactProfileList, KeyProfileList, Legacyprofile, Organizationprofile, ProfileList, Publicprofile, RegularProfileList, University } from "@/payload-types";
import { IGraphQLResponse } from '@/types/graphql';
import { getCollectionSpecificFields, getCollectionSpecificFieldsByVersion } from './fetchDataFromPayload';
import { individualDataFields, metaDataFields } from './queryFields/individualQueryFields';
import { CANARY_MISSION } from '@/enum/enum';


function arrayToString(
    arrayToReduce: (string | null | undefined)[] | undefined | (string | Organizationprofile)[] | (string | University)[] | object[]
) {
    return arrayToReduce && arrayToReduce.reduce((result, item, index) => {
        const itemString =
            typeof item === 'string' ? item : item && 'acronym' in item ? (item?.acronym || '') : item && 'name' in item ? (item?.name || '') : '';
        return result + itemString + (index !== arrayToReduce.length - 1 ? ', ' : '');
    }, '');
}

export function createProfileDetailsForTS(typesenseObj: TypesenseData) {
    const arrayOfData: [{ label: string | null, value: string | null }] = [{ label: null, value: null }]
    if (typeof typesenseObj === 'object' && typesenseObj) {
        const profileStatus = typesenseObj.status ? arrayToString(typesenseObj.status) : null
        profileStatus && arrayOfData.push({ label: 'Status', value: typeof profileStatus === 'string' ? profileStatus : '' })

        const state = typesenseObj.state
        state && arrayOfData.push({ label: 'State', value: state })

        const profileOrganizations = typesenseObj.organizations
            ? arrayToString(typesenseObj.organizations)
            : ''
        profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

        const profileUniversities = typesenseObj.universities
            ? arrayToString(typesenseObj.universities)
            : ''
        profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
    }
    return arrayOfData;
}

export function createProfileDetails(profileData: (string | null) | Legacyprofile | Publicprofile) {
    const arrayOfData: [{ label: string | null, value: string | null }] = [{ label: null, value: null }]
    if (typeof profileData === 'object' && profileData) {
        if ('profileEntity' in profileData && typeof profileData.profileEntity === 'object') {
            const profileStatus = profileData?.profileEntity.status
            profileStatus && arrayOfData.push({ label: 'Status', value: profileStatus })

            let state = null
            if (profileData.profileEntity.country === "usa" && profileData.profileEntity.usaState) {
                state = profileData.profileEntity?.usaState
            }
            else if (profileData.profileEntity.country === "canada" && profileData.profileEntity.canadaState) {
                state = profileData.profileEntity?.canadaState
            }
            const stateWithSpace = state ? state.replace(/([a-z])([A-Z])/g, '$1 $2') : ''
            state && arrayOfData.push({ label: 'State', value: stateWithSpace })

            const organizationArray = profileData?.profileEntity.organizations?.map((org) => {
                return typeof org === 'object' ? org?.name : ''
            })
            const profileOrganizations = profileData?.profileEntity.organizations
                ? arrayToString(
                    organizationArray
                )
                : ''
            profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

            const profileUniversities = profileData?.profileEntity.universities
                ? arrayToString(profileData?.profileEntity.universities)
                : ''
            profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
        }
        if (!('profileEntity' in profileData) && profileData) {
            const profileStatus = profileData.professions ? arrayToString(profileData?.professions?.map((item) => item.profession)) : 'Unknown'

            profileStatus && arrayOfData.push({ label: 'Status', value: typeof profileStatus === 'string' ? profileStatus : '' })
            const state = profileData.state

            state && arrayOfData.push({ label: 'State', value: state })

            const profileOrganizations = profileData?.organizations
                ? arrayToString(
                    profileData.organizations.map((item) => (item.organization ? item.organization : '')),
                )
                : ''
            profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

            const profileUniversities = profileData?.universities
                ? arrayToString(profileData.universities.map((item) => (item.name ? item.name : '')))
                : ''
            profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
        }
    }
    return arrayOfData;
}

export function SortProfileListByName(profileListData: ProfileList | null) {
    if (profileListData?.profileList) {
        const sortedList = [...profileListData.profileList];
        sortedList.sort((a, b) => {
            const aName =
                typeof a.profile?.value === 'object'
                    ? a.profile.relationTo === 'legacyprofiles'
                        ? a.profile.value.fullName
                        : typeof a.profile?.value.profileEntity === 'object'
                            ? a.profile?.value.profileEntity.fullName
                            : ''
                    : ''

            const bName =
                typeof b.profile?.value === 'object'
                    ? b.profile.relationTo === 'legacyprofiles'
                        ? b.profile.value.fullName
                        : typeof b.profile?.value.profileEntity === 'object'
                            ? b.profile?.value.profileEntity.fullName
                            : ''
                    : ''

            return (aName ?? '').localeCompare(bName ?? '', undefined, { sensitivity: 'base' })
        })
        return sortedList;
    }
    return profileListData?.profileList;
}

export function SortProfileListAlphabetically(profileListData: RegularProfileList | CompactProfileList | KeyProfileList | null) {
    if (profileListData?.hasManyProfileList) {
        const sortedList = profileListData.hasManyProfileList;
        sortedList.sort((a, b) => {
            const aName =
                typeof a.value === 'object'
                    ? a.relationTo === 'legacyprofiles'
                        ? a.value.fullName
                        : typeof a.value.profileEntity === 'object'
                            ? a.value.profileEntity.fullName
                            : ''
                    : ''

            const bName =
                typeof b.value === 'object'
                    ? b.relationTo === 'legacyprofiles'
                        ? b.value.fullName
                        : typeof b.value.profileEntity === 'object'
                            ? b.value.profileEntity.fullName
                            : ''
                    : ''

            return (aName ?? '').localeCompare(bName ?? '', undefined, { sensitivity: 'base' })
        })
        return sortedList;
    }
    return profileListData?.hasManyProfileList;
}

export function getFullNameCSS(fullNameLength: number) {
    switch (true) {
        case fullNameLength < 15:
            return 'text-lg'
        case fullNameLength >= 15 && fullNameLength < 18:
            return 'text-base'
        case fullNameLength >= 18 && fullNameLength < 20:
            return 'text-sm'
        case fullNameLength >= 20:
            return 'text-xs'
        default:
            return 'text-lg'
    }
}


export async function getStaticProfilesPage(collectionName: string, staticParamsFields: string, path: string) {
    const data: IGraphQLResponse<Publicprofile, 'Publicprofiles'> =
        (await getCollectionSpecificFields(collectionName, staticParamsFields, `_status: { equals: published }
            ${process.env.DEPLOY_PIPELINE_ENABLED ? ', deployStatus: { equals: publish }' : ''}`)) as IGraphQLResponse<
            Publicprofile,
            'Publicprofiles'
        >
    const allPublicprofileDocs = data.Publicprofiles.docs
    if (data.Publicprofiles.totalDocs === 0) {
        return [{ slug: 'dummy' }]
    } else {
        let filteredDocs
        switch (path) {
            case 'individual':
                filteredDocs = allPublicprofileDocs.filter(
                    (doc) => typeof doc.profileEntity === 'object' && doc.profileEntity.status !== "Professor",
                )
                break;
            case 'professor':
                filteredDocs = allPublicprofileDocs.filter(
                    (doc) => typeof doc.profileEntity === 'object' && doc.profileEntity.status === "Professor",
                )
                break;

        }
        return filteredDocs?.map((doc) => ({ slug: doc.slug }))
    }
}

export async function getMetaDataOfIndividual(individualSlug: string, collectionName: string, path: string) {
    const data: IGraphQLResponse<Publicprofile, 'Publicprofiles'> =
        (await getCollectionSpecificFields(
            collectionName,
            metaDataFields,
            `, slug: { equals: "${individualSlug}"} `,
        )) as IGraphQLResponse<Publicprofile, 'Publicprofiles'>
    const PublicprofileDoc = data.Publicprofiles.docs[0]

    if (!PublicprofileDoc) return {}

    const individualTitle = PublicprofileDoc.meta?.title
        ? `${PublicprofileDoc.meta?.title} - ${CANARY_MISSION.NAME}`
        : `${PublicprofileDoc.title} - ${CANARY_MISSION.NAME}`
    const individualPath = `${path}/${individualSlug}`
    const individualDescription = PublicprofileDoc.meta?.description

    const payload_url = process.env.NEXT_PUBLIC_PAYLOAD_URL ? process.env.NEXT_PUBLIC_PAYLOAD_URL : ''
    const individualCoverImage =
        typeof PublicprofileDoc.meta?.image === 'object' &&
            PublicprofileDoc.meta.image?.sizes?.medium?.url
            ? `${payload_url}${PublicprofileDoc.meta.image?.sizes?.medium?.url}`
            : `${payload_url}${typeof PublicprofileDoc.profileImage === 'object' && PublicprofileDoc.profileImage?.sizes?.medium?.url}`

    return { individualTitle, individualPath, individualDescription, individualCoverImage, PublicprofileDoc }
}

export async function getIndividualData(individualSlug: string, isDraft: boolean, versionId?: string) {

    if (versionId) {
        const data =
            (await getCollectionSpecificFieldsByVersion(
                'versionsPublicprofiles',
                individualDataFields,
                versionId,
                1,
            )) as IGraphQLResponse<{ version: Publicprofile }, 'versionsPublicprofiles'>
        return data.versionsPublicprofiles.docs[0].version

    }
    else {
        const data: IGraphQLResponse<Publicprofile, 'Publicprofiles'> =
            (await getCollectionSpecificFields(
                'Publicprofiles',
                individualDataFields,
                `, slug: { equals: "${individualSlug}"} `,
                1,
                isDraft,
            )) as IGraphQLResponse<Publicprofile, 'Publicprofiles'>
        return data.Publicprofiles.docs[0]
    }
}